"use client";

import type { WorkspaceFileItem } from "@/app/api/__generated__/models/workspaceFileItem";
import { Button } from "@/components/atoms/Button/Button";
import { Icon } from "@/components/atoms/Icon/Icon";
import { Text } from "@/components/atoms/Text/Text";
import {
  Cancel01Icon,
  Delete02Icon,
  Folder01Icon,
} from "@hugeicons/core-free-icons";
import { MoveToFolderDialog } from "../../MoveToFolderDialog/MoveToFolderDialog";
import { useSelectionBar } from "./useSelectionBar";

interface Props {
  selectedFiles: WorkspaceFileItem[];
  totalCount: number;
  onSelectAll: () => void;
  onClear: () => void;
}

export function SelectionBar({
  selectedFiles,
  totalCount,
  onSelectAll,
  onClear,
}: Props) {
  const {
    isMoveOpen,
    setIsMoveOpen,
    isDeleting,
    handleDelete,
    moveSubject,
    sharedFolderId,
  } = useSelectionBar(selectedFiles, onClear);
  const count = selectedFiles.length;

  return (
    <div
      className="flex h-[1.875rem] items-center justify-between gap-3 px-2 pb-2"
      data-testid="artifacts-selection-bar"
    data-sentry-component="SelectionBar" data-sentry-source-file="SelectionBar.tsx">
      <div className="flex items-center gap-3">
        <Text variant="body-medium" as="span" tone="primary" data-sentry-element="Text" data-sentry-source-file="SelectionBar.tsx">
          {count} selected
        </Text>
        {count < totalCount ? (
          <button
            type="button"
            className="text-body text-foreground-tertiary underline-offset-2 hover:text-foreground-strong hover:underline"
            onClick={onSelectAll}
            data-testid="artifacts-select-all"
          >
            Select all {totalCount}
          </button>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="xs"
          onClick={() => setIsMoveOpen(true)}
          data-testid="artifacts-selection-move"
        data-sentry-element="Button" data-sentry-source-file="SelectionBar.tsx">
          <Icon icon={Folder01Icon} size={14} data-sentry-element="Icon" data-sentry-source-file="SelectionBar.tsx" />
          Move to folder
        </Button>
        <Button
          variant="outline"
          size="xs"
          disabled={isDeleting}
          onClick={handleDelete}
          tone="danger"
          // oxlint-disable-next-line design-system/no-restyle -- a red outline action's hover; the destructive variant is a red fill
          className="hover:bg-error-surface hover:text-error-foreground"
          data-testid="artifacts-selection-delete"
        data-sentry-element="Button" data-sentry-source-file="SelectionBar.tsx">
          <Icon icon={Delete02Icon} size={14} data-sentry-element="Icon" data-sentry-source-file="SelectionBar.tsx" />
          {isDeleting ? "Deleting…" : "Delete"}
        </Button>
        <button
          type="button"
          aria-label="Clear selection"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-foreground-tertiary transition-colors hover:bg-surface-hover hover:text-foreground-strong"
          onClick={onClear}
          data-testid="artifacts-selection-clear"
        >
          <Icon icon={Cancel01Icon} size={16} data-sentry-element="Icon" data-sentry-source-file="SelectionBar.tsx" />
        </button>
      </div>
      {isMoveOpen && (
        <MoveToFolderDialog
          move={{
            kind: "files",
            fileIds: selectedFiles.map((file) => file.id),
            currentFolderId: sharedFolderId,
          }}
          subject={moveSubject}
          canMoveToRoot={selectedFiles.some((file) => file.folder_id != null)}
          isOpen={isMoveOpen}
          setIsOpen={setIsMoveOpen}
          onMoved={onClear}
        />
      )}
    </div>
  );
}
